import React, { useState, useEffect, useRef, Suspense, lazy, useCallback } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { supabase } from '../../../lib/supabase';
import { useTenantContext } from '../../../context/TenantContext';
import { MediaSelector } from '../../../components/media/MediaSelector';
import { useToast } from '../../../components/ui/use-toast';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useDomainQuery } from '../../../hooks/useDomainQuery';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { 
  Loader2, Save, ArrowLeft, Image, X, Eye, EyeOff, 
  Plus, Trash2, Upload, ImagePlus, FileImage, FolderOpen
} from 'lucide-react';
import CustomQuillEditor from '../../../components/editor/CustomQuillEditor';
import { fetchAdminArtCategories, fetchAdminArtworkById } from '../../../services/adminPortfolioService';
import { generateSlug } from '../../../lib/utils';
import { PortfolioCategorySelect } from '../../../components/admin/portfolio/PortfolioCategorySelect';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

// Schéma de validation pour le formulaire
const artworkSchema = z.object({
  title: z.string().min(1, 'Le titre est requis'),
  slug: z.string().min(1, 'Le slug est requis'),
  description: z.string().min(1, 'La description est requise'),
  content: z.string().min(1, 'Le contenu est requis'),
  year: z.string().optional(),
  location: z.string().optional(),
  dimensions: z.string().optional(),
  materials: z.string().optional(),
  status: z.enum(['draft', 'published']),
  featured_image: z
    .instanceof(FileList)
    .optional()
    .refine((files) => {
      if (files && files.length > 0) {
        return files[0].size <= MAX_FILE_SIZE;
      }
      return true;
    }, 'La taille maximale du fichier est de 5MB')
    .refine((files) => {
      if (files && files.length > 0) {
        return ACCEPTED_IMAGE_TYPES.includes(files[0].type);
      }
      return true;
    }, 'Format accepté : .jpg, .jpeg, .png, .webp'),
  categories: z.array(z.string()).optional(),
  gallery_images: z.array(z.string()).optional(),
  making_of_images: z.array(z.string()).optional(),
});

type ArtworkFormValues = z.infer<typeof artworkSchema>;

interface Category {
  id: string;
  name: string;
}

export const ArtworkEdit = () => {
  useCheckPermissions(['admin']);
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  
  // Utiliser useDomainQuery au niveau du composant
  const { getFilterParams } = useDomainQuery();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [makingOfImages, setMakingOfImages] = useState<string[]>([]);
  const [showMainImageSelector, setShowMainImageSelector] = useState(false);
  const [showGallerySelector, setShowGallerySelector] = useState(false);
  const [showMakingOfSelector, setShowMakingOfSelector] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isNewProject = !id;

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    console.log('handleImageChange appelé', e.type);
    
    let file: File | null = null;
    
    if ('dataTransfer' in e) {
      console.log('Drag event détecté');
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        file = e.dataTransfer.files[0];
        console.log('Fichier trouvé dans dataTransfer', file.name);
      }
    } else if (e.target.files && e.target.files.length > 0) {
      console.log('Input event détecté');
      file = e.target.files[0];
      console.log('Fichier trouvé dans input', file.name);
    }
    
    if (file) {
      // Vérifier la taille du fichier
      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: 'Erreur',
          description: 'La taille du fichier ne doit pas dépasser 5MB',
          variant: 'destructive',
        });
        return;
      }
      
      // Vérifier le type du fichier
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast({
          title: 'Erreur',
          description: 'Format de fichier non accepté. Utilisez JPG, PNG ou WEBP',
          variant: 'destructive',
        });
        return;
      }
      
      // Créer un FileList pour le formulaire
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      form.setValue('featured_image', dataTransfer.files, { shouldValidate: true });
      
      // Afficher l'aperçu de l'image
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          const imageUrl = event.target.result as string;
          setImagePreview(imageUrl);
          
          // Ajouter automatiquement l'image principale comme première image de la galerie
          const updatedGalleryImages = [imageUrl, ...galleryImages.filter(img => img !== imageUrl)];
          setGalleryImages(updatedGalleryImages);
          form.setValue('gallery_images', updatedGalleryImages);
          
          // S'assurer que le formulaire est marqué comme modifié pour déclencher la validation
          form.trigger('featured_image');
        }
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  // Gérer le changement d'image de galerie
  const handleGalleryImageChange = async (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    let file: File | null = null;
    
    if ('dataTransfer' in e) {
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        file = e.dataTransfer.files[0];
      }
    } else if (e.target.files && e.target.files.length > 0) {
      file = e.target.files[0];
    }
    
    if (file) {
      // Vérifier le type de fichier
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast({
          title: 'Type de fichier non supporté',
          description: 'Veuillez sélectionner une image au format JPG, PNG ou WEBP',
          variant: 'destructive',
        });
        return;
      }
      
      // Vérifier la taille du fichier
      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: 'Fichier trop volumineux',
          description: 'La taille maximale autorisée est de 5MB',
          variant: 'destructive',
        });
        return;
      }
      
      try {
        // Générer un nom de fichier unique
        const timestamp = Date.now();
        const fileExt = file.name.split('.').pop();
        const fileName = `gallery_${timestamp}.${fileExt}`;
        const { tenantId } = getFilterParams();
        const filePath = `tenant/${tenantId}/${fileName}`;
        
        // Téléverser le fichier vers Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('media')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: true
          });

        if (uploadError) throw uploadError;

        // Obtenir l'URL publique
        const { data: publicUrlData } = supabase.storage
          .from('media')
          .getPublicUrl(filePath);

        const imageUrl = publicUrlData.publicUrl;
        
        // Ajouter l'URL de l'image à la galerie
        const updatedGalleryImages = [...galleryImages, imageUrl];
        setGalleryImages(updatedGalleryImages);
        form.setValue('gallery_images', updatedGalleryImages);
        
        // Réinitialiser l'input file
        const fileInput = document.getElementById('gallery-file-input') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
        
        toast({
          title: 'Image ajoutée',
          description: 'L\'image a été ajoutée à la galerie',
        });
      } catch (error) {
        console.error('Erreur lors du téléversement de l\'image:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de téléverser l\'image',
          variant: 'destructive',
        });
      }
    }
  };
  
  // Gérer le changement d'image de making-of
  const handleMakingOfImageChange = async (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    let file: File | null = null;
    
    if ('dataTransfer' in e) {
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        file = e.dataTransfer.files[0];
      }
    } else if (e.target.files && e.target.files.length > 0) {
      file = e.target.files[0];
    }
    
    if (file) {
      // Vérifier le type de fichier
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast({
          title: 'Type de fichier non supporté',
          description: 'Veuillez sélectionner une image au format JPG, PNG ou WEBP',
          variant: 'destructive',
        });
        return;
      }
      
      // Vérifier la taille du fichier
      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: 'Fichier trop volumineux',
          description: 'La taille maximale autorisée est de 5MB',
          variant: 'destructive',
        });
        return;
      }
      
      try {
        // Générer un nom de fichier unique
        const timestamp = Date.now();
        const fileExt = file.name.split('.').pop();
        const fileName = `making_of_${timestamp}.${fileExt}`;
        const { tenantId } = getFilterParams();
        const filePath = `tenant/${tenantId}/${fileName}`;
        
        // Téléverser le fichier vers Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('media')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: true
          });

        if (uploadError) throw uploadError;

        // Obtenir l'URL publique
        const { data: publicUrlData } = supabase.storage
          .from('media')
          .getPublicUrl(filePath);

        const imageUrl = publicUrlData.publicUrl;
        
        // Ajouter l'URL de l'image au making-of
        const updatedMakingOfImages = [...makingOfImages, imageUrl];
        setMakingOfImages(updatedMakingOfImages);
        form.setValue('making_of_images', updatedMakingOfImages);
        
        // Réinitialiser l'input file
        const fileInput = document.getElementById('making-of-file-input') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
        
        toast({
          title: 'Image ajoutée',
          description: 'L\'image a été ajoutée au making-of',
        });
      } catch (error) {
        console.error('Erreur lors du téléversement de l\'image:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de téléverser l\'image',
          variant: 'destructive',
        });
      }
    }
  };
  
  const removeImage = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
      return;
    }

    try {
      // Si on est en mode édition et qu'il y a une image existante
      if (id && imagePreview && imagePreview.startsWith('http')) {
        try {
          // Extraire le chemin complet de l'URL
          const url = new URL(imagePreview);
          const pathParts = url.pathname.split('/');
          const bucketName = pathParts[1]; // 'media' ou 'partners'
          const oldFilePath = pathParts.slice(2).join('/');
          
          if (oldFilePath) {
            await supabase.storage
              .from(bucketName)
              .remove([oldFilePath]);
          }
        } catch (error) {
          console.error("Erreur lors de l'extraction du chemin de l'image:", error);
        }
      }
      
      // Supprimer l'image de la galerie si elle y est présente
      if (imagePreview) {
        const updatedGalleryImages = galleryImages.filter(img => img !== imagePreview);
        setGalleryImages(updatedGalleryImages);
        form.setValue('gallery_images', updatedGalleryImages);
      }
      
      // Réinitialiser l'aperçu et le champ du formulaire
      setImagePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Créer un FileList vide pour le formulaire
      const dataTransfer = new DataTransfer();
      form.setValue('featured_image', dataTransfer.files, { shouldValidate: true });
      
      toast({
        title: 'Image supprimée',
        description: 'L\'image a été supprimée avec succès',
      });
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'image:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer l\'image',
        variant: 'destructive',
      });
    }
  };

  // Initialiser le formulaire
  const form = useForm<ArtworkFormValues>({
    resolver: zodResolver(artworkSchema),
    defaultValues: {
      title: '',
      slug: '',
      description: '',
      content: '',
      year: '',
      location: '',
      dimensions: '',
      materials: '',
      status: 'draft' as const,
      categories: [],
      gallery_images: [],
      making_of_images: [],
    },
  });

  // Récupérer tenantId et domain depuis useDomainQuery
  const { tenantId, domain } = getFilterParams();
  
  // Charger les catégories
  const loadCategories = async () => {
    try {
      const categoriesData = await fetchAdminArtCategories(tenantId);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les catégories',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    loadCategories();
  }, [toast, tenantId]);

  // Charger le projet si on est en mode édition
  useEffect(() => {
    const loadArtwork = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const artwork = await fetchAdminArtworkById(id);
        
        if (!artwork) {
          toast({
            title: 'Erreur',
            description: 'Élément non trouvé',
            variant: 'destructive',
          });
          navigate('/admin/portfolio');
          return;
        }

        // Remplir le formulaire avec les données du projet
        form.reset({
          title: artwork.title,
          slug: artwork.slug,
          description: artwork.description || '',
          content: artwork.content || '',
          year: artwork.year || '',
          location: artwork.location || '',
          dimensions: artwork.dimensions || '',
          materials: artwork.materials ? artwork.materials.join(', ') : '',
          status: artwork.status as 'draft' | 'published',
        });

        // Définir l'aperçu de l'image
        if (artwork.featured_image_url) {
          setImagePreview(artwork.featured_image_url);
        }

        // Définir les catégories sélectionnées
        if (artwork.categories && artwork.categories.length > 0) {
          const categoryIds = artwork.categories.map((cat: any) => cat.id);
          setSelectedCategories(categoryIds);
          form.setValue('categories', categoryIds);
        }

        // Définir les images de la galerie et du making-of
        if (artwork.gallery_images && artwork.gallery_images.length > 0) {
          setGalleryImages(artwork.gallery_images);
        }

        if (artwork.making_of_images && artwork.making_of_images.length > 0) {
          setMakingOfImages(artwork.making_of_images);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'œuvre:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger l\'œuvre',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadArtwork();
  }, [id, navigate, toast, form]);

  // Gérer le changement de titre pour générer le slug
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    form.setValue('title', title);
    
    // Générer le slug uniquement pour les nouvelles œuvres ou si le slug est vide
    if (isNewProject || !form.getValues('slug')) {
      const slug = generateSlug(title);
      form.setValue('slug', slug);
    }
  };

  // Gérer le changement de catégories

  // Gérer le changement de catégories
  const handleCategoryChange = (categoryId: string) => {
    const updatedCategories = selectedCategories.includes(categoryId)
      ? selectedCategories.filter(id => id !== categoryId)
      : [...selectedCategories, categoryId];
    
    setSelectedCategories(updatedCategories);
    form.setValue('categories', updatedCategories);
  };
  
  // Gérer l'ajout d'une image à la galerie
  const handleAddGalleryImage = (url: string) => {
    if (url && url.trim() !== '') {
      const updatedGalleryImages = [...galleryImages, url.trim()];
      setGalleryImages(updatedGalleryImages);
      form.setValue('gallery_images', updatedGalleryImages);
    }
  };
  
  // Gérer la suppression d'une image de la galerie
  const removeGalleryImage = async (index: number) => {
    const imageToRemove = galleryImages[index];
    
    // Si l'image est une URL Supabase, la supprimer du stockage
    if (imageToRemove && imageToRemove.startsWith('http')) {
      try {
        // Extraire le chemin complet de l'URL
        const url = new URL(imageToRemove);
        const pathParts = url.pathname.split('/');
        const bucketName = pathParts[1]; // 'media' ou 'partners'
        const oldFilePath = pathParts.slice(2).join('/');
        
        if (oldFilePath) {
          await supabase.storage
            .from(bucketName)
            .remove([oldFilePath]);
        }
      } catch (error) {
        console.error("Erreur lors de la suppression de l'image:", error);
      }
    }
    
    const updatedGalleryImages = [...galleryImages];
    updatedGalleryImages.splice(index, 1);
    setGalleryImages(updatedGalleryImages);
    form.setValue('gallery_images', updatedGalleryImages);
    
    toast({
      title: 'Image supprimée',
      description: 'L\'image a été supprimée de la galerie',
    });
  };
  
  // Gérer l'ajout d'une image au making-of
  const handleAddMakingOfImage = (url: string) => {
    if (url && url.trim() !== '') {
      const updatedMakingOfImages = [...makingOfImages, url.trim()];
      setMakingOfImages(updatedMakingOfImages);
      form.setValue('making_of_images', updatedMakingOfImages);
    }
  };
  
  // Gérer la suppression d'une image du making-of
  const removeMakingOfImage = async (index: number) => {
    const imageToRemove = makingOfImages[index];
    
    // Si l'image est une URL Supabase, la supprimer du stockage
    if (imageToRemove && imageToRemove.startsWith('http')) {
      try {
        // Extraire le chemin complet de l'URL
        const url = new URL(imageToRemove);
        const pathParts = url.pathname.split('/');
        const bucketName = pathParts[1]; // 'media' ou 'partners'
        const oldFilePath = pathParts.slice(2).join('/');
        
        if (oldFilePath) {
          await supabase.storage
            .from(bucketName)
            .remove([oldFilePath]);
        }
      } catch (error) {
        console.error("Erreur lors de la suppression de l'image:", error);
      }
    }
    
    const updatedMakingOfImages = [...makingOfImages];
    updatedMakingOfImages.splice(index, 1);
    setMakingOfImages(updatedMakingOfImages);
    form.setValue('making_of_images', updatedMakingOfImages);
    
    toast({
      title: 'Image supprimée',
      description: 'L\'image a été supprimée du making-of',
    });
  };
  
  // Gérer la soumission du formulaire
  const onSubmit = async (values: ArtworkFormValues) => {
    setSaving(true);
    
    try {
      // Préparer les données de base
      const { tenantId, domain } = getFilterParams();
      const now = new Date().toISOString();
      
      // Convertir les matériaux en tableau
      const materialsArray = values.materials
        ? values.materials.split(',').map(m => m.trim()).filter(Boolean)
        : [];
      
      // Filtrer les images de galerie et making-of pour ne conserver que les URLs valides
      const filteredGalleryImages = galleryImages.filter(url => url.startsWith('http'));
      const filteredMakingOfImages = makingOfImages.filter(url => url.startsWith('http'));
      
      // Préparer les données de l'œuvre
      const artworkData: any = {
        title: values.title,
        slug: values.slug,
        description: values.description,
        content: values.content,
        year: values.year || null,
        location: values.location || null,
        dimensions: values.dimensions || null,
        materials: materialsArray,
        gallery_images: filteredGalleryImages,
        making_of_images: filteredMakingOfImages,
        status: values.status,
        tenant_id: tenantId,
        domain: domain,
        updated_at: now,
      };
      
      // Si c'est une nouvelle œuvre, ajouter la date de création
      if (isNewProject) {
        artworkData.created_at = now;
      }
      
      // Créer ou mettre à jour l'œuvre dans la base de données
      let artwork;
      if (isNewProject) {
        const { data, error } = await supabase
          .from('portfolio_artworks')
          .insert(artworkData)
          .select()
          .single();
        
        if (error) throw error;
        artwork = data;
      } else {
        const { data, error } = await supabase
          .from('portfolio_artworks')
          .update(artworkData)
          .eq('id', id)
          .select()
          .single();
        
        if (error) throw error;
        artwork = data;
      }
    
      // Gérer le téléversement de l'image si une image a été sélectionnée
      let featuredImageUrl = imagePreview && !imagePreview.startsWith('http') ? null : imagePreview;
      
      // Si une nouvelle image a été sélectionnée
      if (values.featured_image && values.featured_image.length > 0) {
        const file = values.featured_image[0];
        const fileExt = file.name.split('.').pop();
        const fileName = `${artwork.id}.${fileExt}`;
        const { tenantId } = getFilterParams();
        const filePath = `tenant/${tenantId}/${fileName}`;
        
        // Supprimer l'ancienne image si elle existe
        if (imagePreview && imagePreview.startsWith('http')) {
          try {
            // Extraire le chemin complet de l'URL
            const url = new URL(imagePreview);
            const pathParts = url.pathname.split('/');
            const bucketName = pathParts[1]; // 'media' ou 'partners'
            const oldFilePath = pathParts.slice(2).join('/');
            
            if (oldFilePath) {
              await supabase.storage
                .from(bucketName)
                .remove([oldFilePath]);
            }
          } catch (error) {
            console.error("Erreur lors de la suppression de l'ancienne image:", error);
          }
        }
        
        // Téléverser le nouveau fichier
        const { error: uploadError } = await supabase.storage
          .from('media')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: true
          });

        if (uploadError) throw uploadError;

        // Obtenir l'URL publique
        const { data: publicUrlData } = supabase.storage
          .from('media')
          .getPublicUrl(filePath);

        featuredImageUrl = publicUrlData.publicUrl;
        
        // Mettre à jour l'aperçu de l'image
        setImagePreview(featuredImageUrl);
        
        // S'assurer que l'image principale est dans la galerie
        if (featuredImageUrl && !galleryImages.includes(featuredImageUrl)) {
          const updatedGalleryImages = [featuredImageUrl, ...galleryImages];
          setGalleryImages(updatedGalleryImages);
          artworkData.gallery_images = updatedGalleryImages;
        }
      }
      
      // Mettre à jour l'œuvre avec l'URL de l'image
      const { error: updateError } = await supabase
        .from('portfolio_artworks')
        .update({ 
          featured_image_url: featuredImageUrl,
          updated_at: new Date().toISOString() 
        })
        .eq('id', artwork.id);
          
      if (updateError) throw updateError;
      
      // Mettre à jour l'aperçu de l'image
      if (featuredImageUrl) {
        artwork.featured_image_url = featuredImageUrl;
        setImagePreview(featuredImageUrl);
      }
      
      // Mettre à jour les catégories de l'œuvre
      if (values.categories && values.categories.length > 0) {
        // Supprimer les associations existantes
        const { error: deleteError } = await supabase
          .from('portfolio_artwork_categories')
          .delete()
          .eq('artwork_id', artwork.id);
        
        if (deleteError) throw deleteError;
        
        // Créer les nouvelles associations
        const categoryAssociations = values.categories.map(categoryId => ({
          artwork_id: artwork.id,
          category_id: categoryId,
          created_at: now
        }));
        
        const { error: insertError } = await supabase
          .from('portfolio_artwork_categories')
          .insert(categoryAssociations);
        
        if (insertError) throw insertError;
      }
      
      // Afficher un message de succès
      toast({
        title: 'Succès',
        description: isNewProject 
          ? 'Œuvre créée avec succès' 
          : 'Œuvre mise à jour avec succès',
      });
      
      // Rediriger vers la liste des projets
      navigate('/admin/portfolio');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder l\'\u0153uvre',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="container mx-auto py-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/admin/portfolio')}
              className="border-white/10 text-white hover:bg-white/5"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Retour
            </Button>
            <h1 className="text-3xl font-bold text-white">
              {isNewProject ? 'Nouveau projet' : 'Modifier le projet'}
            </h1>
          </div>
          
          <div className="flex gap-2">
            {!isNewProject && form.watch('status') === 'published' && (
              <Button 
                variant="outline" 
                size="sm" 
                asChild
                className="border-white/10 text-white hover:bg-white/5"
              >
                <Link to={`/portfolio/${form.watch('slug')}`} target="_blank">
                  <Eye className="mr-2 h-4 w-4" /> Voir
                </Link>
              </Button>
            )}
            <Button 
              type="submit"
              form="artwork-form"
              disabled={saving || loading}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enregistrement...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" /> Enregistrer
                </>
              )}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="animate-spin h-8 w-8 text-yellow-500 mr-3" />
            <span className="text-white text-lg">Chargement...</span>
          </div>
        ) : (
          <Form {...form}>
            <form 
              id="artwork-form"
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Colonne principale */}
                <div className="md:col-span-2 space-y-6">
                  {/* Informations de base */}
                  <Card className="bg-black/20 backdrop-blur-sm border border-white/10 shadow-lg">
                    <CardContent className="p-6 space-y-4">
                      <h2 className="text-xl font-semibold text-white mb-4">Informations de base</h2>
                      
                      <FormField
                        control={form.control}
                        name="title"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white">Titre</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Titre de l'œuvre" 
                                {...field} 
                                onChange={handleTitleChange}
                                className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="slug"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white">Slug</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="slug-de-l-oeuvre" 
                                {...field} 
                                className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                              />
                            </FormControl>
                            <FormDescription className="text-gray-400">
                              L'identifiant unique pour l'URL (généré automatiquement)
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white">Description courte</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="Brève description de l'œuvre" 
                                {...field} 
                                rows={3}
                                className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                              />
                            </FormControl>
                            <FormDescription className="text-gray-400">
                              Une courte description qui apparaîtra dans les listes et aperçus
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>
                  
                  {/* Contenu détaillé */}
                  <Card className="bg-black/20 backdrop-blur-sm border border-white/10 shadow-lg">
                    <CardContent className="p-6 space-y-4">
                      <h2 className="text-xl font-semibold text-white mb-4">Contenu détaillé</h2>
                      
                      <FormField
                        control={form.control}
                        name="content"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white">Contenu</FormLabel>
                            <FormControl>
                              <CustomQuillEditor
                                value={field.value}
                                onChange={field.onChange}
                                className="bg-black text-white rounded-md [&_.ql-toolbar]:bg-gray-900 [&_.ql-container]:bg-black [&_.ql-editor]:min-h-[200px] [&_.ql-editor]:text-white [&_.ql-toolbar_button]:text-white [&_.ql-toolbar_button]:hover:bg-gray-800 [&_.ql-toolbar_button.ql-active]:bg-yellow-500 [&_.ql-toolbar_button.ql-active]:text-black [&_.ql-border-bottom]:border-gray-800 [&_.ql-stroke]:stroke-white [&_.ql-fill]:fill-white"
                              />
                            </FormControl>
                            <FormDescription className="text-gray-400 mt-2">
                              Description détaillée de l'œuvre, processus de création, inspiration, etc.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>
                  
                  {/* Caractéristiques techniques */}
                  <Card className="bg-black/20 backdrop-blur-sm border border-white/10 shadow-lg">
                    <CardContent className="p-6 space-y-4">
                      <h2 className="text-xl font-semibold text-white mb-4">Caractéristiques techniques</h2>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="year"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-white">Année</FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="2023" 
                                  {...field} 
                                  className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="dimensions"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-white">Dimensions</FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="100 x 80 cm" 
                                  {...field} 
                                  className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="location"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-white">Lieu</FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="Paris, France" 
                                  {...field} 
                                  className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="materials"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-white">Matériaux</FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="Acrylique, toile" 
                                  {...field} 
                                  className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                                />
                              </FormControl>
                              <FormDescription className="text-gray-400">
                                Séparez les matériaux par des virgules
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Colonne latérale */}
                <div className="space-y-6">
                  {/* Statut et publication */}
                  <Card className="bg-black/20 backdrop-blur-sm border border-white/10 shadow-lg">
                    <CardContent className="p-6 space-y-4">
                      <h2 className="text-xl font-semibold text-white mb-4">Publication</h2>
                      
                      <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                          <FormItem className="space-y-3">
                            <FormLabel className="text-white">Statut</FormLabel>
                            <div className="flex flex-col space-y-2">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="radio"
                                  id="draft"
                                  value="draft"
                                  checked={field.value === 'draft'}
                                  onChange={() => form.setValue('status', 'draft')}
                                  className="h-4 w-4 text-yellow-500 focus:ring-yellow-500"
                                />
                                <label htmlFor="draft" className="text-white">
                                  <div className="flex items-center">
                                    <EyeOff className="h-4 w-4 mr-2 text-gray-400" />
                                    Brouillon
                                  </div>
                                </label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="radio"
                                  id="published"
                                  value="published"
                                  checked={field.value === 'published'}
                                  onChange={() => form.setValue('status', 'published')}
                                  className="h-4 w-4 text-yellow-500 focus:ring-yellow-500"
                                />
                                <label htmlFor="published" className="text-white">
                                  <div className="flex items-center">
                                    <Eye className="h-4 w-4 mr-2 text-green-400" />
                                    Publié
                                  </div>
                                </label>
                              </div>
                            </div>
                            <FormDescription className="text-gray-400">
                              Les brouillons ne sont pas visibles sur le site public
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>
                  
                  {/* Image principale */}
                  <Card className="bg-black/20 backdrop-blur-sm border border-white/10 shadow-lg">
                    <CardContent className="p-6 space-y-4">
                      <h2 className="text-xl font-semibold text-white mb-4">Image principale</h2>
                      
                      <FormField
                        control={form.control}
                        name="featured_image"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white">Image</FormLabel>
                            <FormControl>
                              <div className="space-y-4">
                                {imagePreview ? (
                                  <div className="relative">
                                    <img 
                                      src={imagePreview} 
                                      alt="Aperçu" 
                                      className="w-full h-auto rounded-lg border border-white/10"
                                    />
                                    <Button
                                      type="button"
                                      variant="destructive"
                                      size="sm"
                                      onClick={removeImage}
                                      className="absolute top-2 right-2 bg-[#D4AF37] hover:bg-[#D4AF37]/80 transition-colors duration-200"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div 
                                  className="border-2 border-dashed border-white/10 rounded-lg p-8 text-center bg-white/5 hover:border-yellow-500/50 transition-colors cursor-pointer"
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleImageChange(e);
                                  }}
                                  onDragOver={handleDragOver}
                                  onClick={() => {
                                    console.log('Zone de dépôt cliquée');
                                    console.log('fileInputRef existe:', !!fileInputRef.current);
                                    fileInputRef.current?.click();
                                  }}
                                >
                                  <ImagePlus className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                                  <p className="text-white font-medium">Glissez-déposez une image ou cliquez pour sélectionner</p>
                                  <p className="text-gray-400 text-sm mt-1">JPG, PNG, WEBP. 5MB max.</p>
                                </div>
                                )}
                                <div className="flex space-x-2 mt-4">
                                  <Button 
                                    type="button" 
                                    onClick={() => fileInputRef.current?.click()}
                                    className="flex-1 text-black"
                                  >
                                    <Upload className="h-4 w-4 mr-2" /> Uploader
                                  </Button>
                                  <Button 
                                    type="button" 
                                    onClick={() => setShowMainImageSelector(true)}
                                    className="flex-1 bg-gray-700 hover:bg-gray-600 text-white"
                                  >
                                    <FolderOpen className="h-4 w-4 mr-2" /> Médiathèque
                                  </Button>
                                </div>
                                <input
                                  id="featured-image-input"
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp"
                                  onChange={handleImageChange}
                                  className="hidden"
                                  ref={fileInputRef}
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>
                  
                  {/* Sélecteur de média pour l'image principale */}
                  <MediaSelector
                    isOpen={showMainImageSelector}
                    onClose={() => setShowMainImageSelector(false)}
                    onSelect={(url) => {
                      if (typeof url === 'string') {
                        setImagePreview(url);
                        // Créer un FileList vide pour le formulaire
                        form.setValue('featured_image', undefined);
                        setShowMainImageSelector(false);
                      }
                    }}
                    selectedUrl={imagePreview || ''}
                    title="Sélectionner une image principale"
                    allowUpload={true}
                  />
                  
                  {/* Images de galerie */}
                  <Card className="bg-black/20 backdrop-blur-sm border border-white/10 shadow-lg">
                    <CardContent className="p-6 space-y-4">
                      <h2 className="text-xl font-semibold text-white mb-4">Images de galerie</h2>
                      
                      <div className="space-y-4">
                        {galleryImages.length > 0 && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {galleryImages.map((image, index) => (
                              <div key={index} className="relative">
                                <img 
                                  src={image} 
                                  alt={`Galerie ${index + 1}`} 
                                  className="w-full h-auto rounded-lg border border-white/10"
                                />
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => removeGalleryImage(index)}
                                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Zone de glisser-déposer pour les images de galerie */}
                        <div 
                          className="border-2 border-dashed border-white/20 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-yellow-500/50 transition-colors"
                          onClick={() => document.getElementById('gallery-file-input')?.click()}
                          onDragOver={handleDragOver}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleGalleryImageChange(e);
                          }}
                        >
                          <FileImage className="h-10 w-10 text-yellow-500 mb-2" />
                          <p className="text-sm text-gray-300 mb-1">Glissez-déposez une image ou cliquez pour parcourir</p>
                          <p className="text-xs text-gray-400">JPG, PNG ou WEBP, max 5MB</p>
                          <input
                            type="file"
                            id="gallery-file-input"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            className="hidden"
                            onChange={(e) => handleGalleryImageChange(e)}
                          />
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Input 
                            type="text" 
                            placeholder="URL de l'image" 
                            id="gallery-image-url"
                            className="bg-white/10 border-white/10 text-white"
                          />
                          <Button 
                            type="button" 
                            onClick={() => {
                              const input = document.getElementById('gallery-image-url') as HTMLInputElement;
                              handleAddGalleryImage(input.value);
                              input.value = '';
                            }}
                            className="bg-yellow-500 hover:bg-yellow-600 text-black"
                          >
                            <Plus className="h-4 w-4 mr-2" /> Ajouter
                          </Button>
                        </div>
                        
                        <div className="flex space-x-2 mt-4">
                          <Button 
                            type="button" 
                            onClick={() => document.getElementById('gallery-file-input')?.click()}
                            className="flex-1 text-black"
                          >
                            <Upload className="h-4 w-4 mr-2" /> Uploader
                          </Button>
                          <Button 
                            type="button" 
                            onClick={() => setShowGallerySelector(true)}
                            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white"
                          >
                            <FolderOpen className="h-4 w-4 mr-2" /> Médiathèque
                          </Button>
                        </div>
                        
                        <FormDescription className="text-gray-400 mt-2">
                          Ajoutez des URLs d'images pour la galerie de l'œuvre
                        </FormDescription>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Sélecteur de média pour la galerie */}
                  <MediaSelector
                    isOpen={showGallerySelector}
                    onClose={() => setShowGallerySelector(false)}
                    onSelect={(urls) => {
                      // Remplacer la galerie par les images sélectionnées
                      if (Array.isArray(urls)) {
                        setGalleryImages(urls);
                        form.setValue('gallery_images', urls);
                      } else {
                        setGalleryImages([urls]);
                        form.setValue('gallery_images', [urls]);
                      }
                      setShowGallerySelector(false);
                    }}
                    selectedUrls={galleryImages}
                    title="Sélectionner des images pour la galerie"
                    allowUpload={true}
                    multiSelect={true}
                  />
                  
                  {/* Images de making-of */}
                  <Card className="bg-black/20 backdrop-blur-sm border border-white/10 shadow-lg">
                    <CardContent className="p-6 space-y-4">
                      <h2 className="text-xl font-semibold text-white mb-4">Images de making-of</h2>
                      
                      <div className="space-y-4">
                        {makingOfImages.length > 0 && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {makingOfImages.map((image, index) => (
                              <div key={index} className="relative">
                                <img 
                                  src={image} 
                                  alt={`Making-of ${index + 1}`} 
                                  className="w-full h-auto rounded-lg border border-white/10"
                                />
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => removeMakingOfImage(index)}
                                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Zone de glisser-déposer pour les images de making-of */}
                        <div 
                          className="border-2 border-dashed border-white/20 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-yellow-500/50 transition-colors"
                          onClick={() => document.getElementById('making-of-file-input')?.click()}
                          onDragOver={handleDragOver}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleMakingOfImageChange(e);
                          }}
                        >
                          <FileImage className="h-10 w-10 text-yellow-500 mb-2" />
                          <p className="text-sm text-gray-300 mb-1">Glissez-déposez une image ou cliquez pour parcourir</p>
                          <p className="text-xs text-gray-400">JPG, PNG ou WEBP, max 5MB</p>
                          <input
                            type="file"
                            id="making-of-file-input"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            className="hidden"
                            onChange={(e) => handleMakingOfImageChange(e)}
                          />
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Input 
                            type="text" 
                            placeholder="URL de l'image" 
                            id="making-of-image-url"
                            className="bg-white/10 border-white/10 text-white"
                          />
                          <Button 
                            type="button" 
                            onClick={() => {
                              const input = document.getElementById('making-of-image-url') as HTMLInputElement;
                              handleAddMakingOfImage(input.value);
                              input.value = '';
                            }}
                            className="bg-yellow-500 hover:bg-yellow-600 text-black"
                          >
                            <Plus className="h-4 w-4 mr-2" /> Ajouter
                          </Button>
                        </div>
                        
                        <div className="flex space-x-2 mt-4">
                          <Button 
                            type="button" 
                            onClick={() => document.getElementById('making-of-file-input')?.click()}
                            className="flex-1 text-black"
                          >
                            <Upload className="h-4 w-4 mr-2" /> Uploader
                          </Button>
                          <Button 
                            type="button" 
                            onClick={() => setShowMakingOfSelector(true)}
                            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white"
                          >
                            <FolderOpen className="h-4 w-4 mr-2" /> Médiathèque
                          </Button>
                        </div>
                        
                        <FormDescription className="text-gray-400 mt-2">
                          Ajoutez des URLs d'images pour le making-of de l'œuvre
                        </FormDescription>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Sélecteur de média pour le making-of */}
                  <MediaSelector
                    isOpen={showMakingOfSelector}
                    onClose={() => setShowMakingOfSelector(false)}
                    onSelect={(urls) => {
                      // Remplacer le making-of par les images sélectionnées
                      if (Array.isArray(urls)) {
                        setMakingOfImages(urls);
                        form.setValue('making_of_images', urls);
                      } else {
                        setMakingOfImages([urls]);
                        form.setValue('making_of_images', [urls]);
                      }
                      setShowMakingOfSelector(false);
                    }}
                    selectedUrls={makingOfImages}
                    title="Sélectionner des images pour le making-of"
                    allowUpload={true}
                    multiSelect={true}
                  />
                  
                  {/* Catégories */}
                  <Card className="bg-black/20 backdrop-blur-sm border border-white/10 shadow-lg">
                    <CardContent className="p-6 space-y-4">
                      <h2 className="text-xl font-semibold text-white mb-4">Catégories</h2>
                      
                      <FormField
                        control={form.control}
                        name="categories"
                        render={() => (
                          <FormItem>
                            <PortfolioCategorySelect
                              categories={categories}
                              selectedCategories={selectedCategories}
                              onChange={(selectedIds) => {
                                setSelectedCategories(selectedIds);
                                form.setValue('categories', selectedIds);
                              }}
                              onCategoryCreated={(newCategory) => {
                                // Recharger les catégories après création
                                loadCategories();
                              }}
                            />
                            <FormDescription className="text-gray-400 mt-2">
                              Sélectionnez les catégories associées à cette œuvre
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>
                </div>
              </div>
            </form>
          </Form>
        )}
      </div>
    </AdminLayout>
  );
};

export default ArtworkEdit;
